// src/components/results/QuestionPanel.jsx
//
// The question behind one answer-sheet square, opened in place below the Teil cards.
// It is remounted for every square, so the motion replays: the question writes itself
// in, the options pop up, the red pen strikes the student's choice and the teal tick
// lands on the right one. Then the trap, the deciding quote and a next step.
import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Lock, X } from 'lucide-react';
import { isAnswerCorrect } from '../../utils/answerCheck';
import { questionPeer } from './peerText';
import { explainCause, familyEntry, labelOf, panelOptions, pick, practiceLinks, questionText } from './storyText';

const HAND = { fontFamily: "'Caveat', cursive" };

export default function QuestionPanel({ item, q, part, where, idx, exam, formatAnswer, isFree, onPractice, onLocked, onClose, scroll = true, norms }) {
  const { t, i18n } = useTranslation();
  const lang = (i18n.language || 'en').split('-')[0];
  const ref = useRef(null);
  const wrong = !item.correct;
  const cause = wrong ? explainCause(item, idx, lang, t) : null;
  const text = questionText(q, part) ?? t('examResults.story.whichHeading', { n: q.questionNumber });
  const options = panelOptions(item, q, part, formatAnswer, isAnswerCorrect);
  const key = options.find((o) => o.isKey);
  const note = item.correct ? '✓' : (key && key.label.length <= 16 ? `→ ${key.label}` : '✗');
  const practiceEntry = item.point ? idx.point[item.point] : familyEntry(idx, 'skill', item.skill);
  const links = wrong ? practiceLinks(practiceEntry, exam.level, exam.examProvider, t, item.section) : [];
  const skillLabel = item.point ? pick(idx.point[item.point]?.label, lang) : labelOf(idx, 'skill', item.skill, lang);
  const accent = wrong ? '#DC2626' : '#006D77';
  const tickAt = wrong ? 1.25 : 0.8;
  const peer = questionPeer(norms, exam.examFileName, item);

  // Bring the panel into view without jumping when it is already visible.
  useEffect(() => {
    if (!scroll) return;
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    ref.current?.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'nearest' });
  }, [scroll]);

  return (
    <div
      ref={ref}
      id="rs-question-panel"
      role="region"
      aria-label={where}
      aria-live="polite"
      className="rs-panel rs-paper scroll-mt-28 flex flex-wrap gap-x-8 gap-y-3.5 px-5 sm:px-6 pt-4 pb-6"
      style={{ borderTop: `6px solid ${accent}`, backgroundPosition: '0 46px' }}
    >
      <div className="flex-[999_1_460px] min-w-0 flex flex-col gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[13px] font-bold text-stone-600">{where}</span>
          {item.confident && (
            <span className="rs-later text-xs font-bold px-2.5 py-1 rounded-full bg-stone-900 text-white" style={{ animationDelay: '1.2s' }}>
              {t('examResults.story.sureLook')}
            </span>
          )}
          {item.flagged && <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-amber-200 text-amber-900">{t('examResults.story.youFlagged')}</span>}
        </div>
        {text && <p className="m-0 text-lg sm:text-xl font-semibold leading-snug"><span className="rs-write">{text}</span></p>}

        <div className="flex flex-wrap gap-2.5">
          {options.map((o, i) => {
            const look = o.isKey
              ? 'bg-[#E6F4F1] text-[#006D77] border-[#006D77]'
              : o.isYours ? 'bg-white text-red-800 border-red-600' : 'bg-stone-100 text-stone-500 border-transparent';
            return (
              <span key={String(o.value)} className={`rs-opt relative inline-flex items-center gap-2 min-h-[44px] px-4 py-1.5 rounded-[22px] border-2 font-bold text-[15px] leading-snug ${look}`} style={{ animationDelay: `${0.5 + i * 0.12}s` }}>
                {o.label}
                {o.isYours && !o.isKey && (
                  <svg viewBox="0 0 100 20" preserveAspectRatio="none" className="absolute left-2.5 right-2.5 top-0 w-[calc(100%-20px)] h-full overflow-visible" aria-hidden="true">
                    <path className="rs-stroke rs-draw" pathLength="1" stroke="#DC2626" strokeWidth="3" d="M2 13 C 30 7, 70 9, 98 6" style={{ animationDelay: '1s', animationDuration: '.45s' }} />
                  </svg>
                )}
                {o.isKey && (
                  <svg viewBox="0 0 24 20" className="shrink-0 w-5 h-[17px] overflow-visible" aria-hidden="true">
                    <path className="rs-stroke rs-draw" pathLength="1" stroke="#006D77" strokeWidth="3" d="M2 11 l6 6 l14 -15" style={{ animationDelay: `${tickAt}s`, animationDuration: '.4s' }} />
                  </svg>
                )}
                <span className="sr-only">
                  {o.isYours ? ` — ${t('examResults.yourAnswer')}` : ''}{o.isKey ? ` — ${t('examResults.correctAnswer')}` : ''}
                </span>
              </span>
            );
          })}
          {wrong && !item.answered && (
            <span className="rs-opt inline-flex items-center min-h-[44px] px-4 rounded-[22px] border-2 border-dashed border-red-600 text-red-800 font-bold text-[15px]" style={{ animationDelay: '.5s' }}>
              {t('examResults.noAnswer')}
            </span>
          )}
        </div>

        {wrong ? (
          <div className="rs-later flex flex-col gap-2" style={{ animationDelay: '1.35s' }}>
            <div className="flex flex-wrap gap-2">
              {cause.label && <span className="text-[13px] font-bold px-2.5 py-1.5 rounded-full bg-red-100 text-red-800">{cause.label}</span>}
              {skillLabel && <span className="text-[13px] font-semibold px-2.5 py-1.5 rounded-full bg-stone-100 text-stone-700">{skillLabel}</span>}
            </div>
            {cause.why && <p className="m-0 text-[15px] leading-relaxed">{cause.why}</p>}
            {peer && <p className="m-0 text-sm font-semibold text-[#006D77]">{t(peer.key, { pct: peer.pct })}</p>}
            {item.evidence && (
              <p className="m-0 text-[15px] leading-relaxed text-stone-700">
                {t('examResults.story.textSays')}{' '}
                <span className="italic underline decoration-red-600 decoration-2 underline-offset-4">„{item.evidence}“</span>
              </p>
            )}
            {cause.tip && <p className="m-0 text-sm text-stone-700"><strong>{t('examResults.story.tip')}</strong> {cause.tip}</p>}
            {links.length > 0 && (
              <div className="flex flex-wrap gap-2.5 mt-1">
                {links.map((l) => {
                  const locked = l.premium && isFree;
                  return (
                    <button key={l.label} type="button" onClick={() => (locked ? onLocked() : onPractice(l))}
                      className={`rs-focus min-h-[44px] px-4 rounded-[10px] text-sm font-semibold inline-flex items-center gap-2 touch-manipulation active:scale-[0.98] transition-transform ${l.premium ? 'bg-stone-900 text-white' : 'border-2 border-stone-900 text-stone-900 bg-white'}`}>
                      {locked && <Lock className="w-4 h-4" aria-hidden="true" />}
                      {l.label}{locked ? ` · ${t('examResults.story.premium')}` : ''}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        ) : (
          <p className="rs-later m-0 text-[15px] font-semibold text-[#006D77]" style={{ animationDelay: '1s' }}>
            {item.flagged ? t('examResults.story.flaggedRight') : t('examResults.story.right')}
            {peer && <> {t(peer.key, { pct: peer.pct })}</>}
          </p>
        )}
      </div>

      <div className="flex-[1_1_120px] flex flex-row-reverse sm:flex-col items-start sm:items-end justify-between gap-2">
        <button type="button" onClick={onClose} aria-label={t('examResults.story.close')}
          className="rs-focus w-11 h-11 shrink-0 rounded-full bg-white border border-stone-300 inline-flex items-center justify-center text-stone-700 touch-manipulation">
          <X className="w-5 h-5" aria-hidden="true" />
        </button>
        <span className="rs-ink text-[40px] leading-none whitespace-nowrap" style={{ ...HAND, color: accent, animationDelay: '1.5s' }} aria-hidden="true">{note}</span>
      </div>
    </div>
  );
}
